import { Controller } from "@hotwired/stimulus"

// Record an outside call (pmfit K7-36, Phelipe 2026-10-08). Keane's state
// agencies meet in Microsoft Teams, which ConsLog can't join, so ConsLog
// records the call's sound on his own computer:
//   1. The browser shares the screen or tab with the call, with its sound.
//      Only the sound is kept: the picture is never recorded.
//   2. That sound plus his microphone is recorded in 30-second pieces, each
//      sent straight to S3 (presign → PUT → register), so a crash loses one
//      piece at most.
//   3. On Stop the server turns the pieces into the transcript and minutes
//      (Meeting::ProcessCallRecordingJob) of the meeting Start made, and the
//      bar follows along until they're in.
// The recorder sits in the page layout and is turbo-permanent, like the
// Feedback recorder: the call keeps recording while he moves around ConsLog
// (Phelipe: "it should allow for that"). Closing the tab asks first. While it
// records, the bar shows the time and a sound wave for the call and for his
// microphone; "Pop out" floats it above every other window (Document
// Picture-in-Picture), on top of Teams.
// The New meeting menu's item is this controller as a launcher; given a
// recording to watch, it is the "being turned into minutes" banner on the
// meeting page.
const PIECE_MS = 30_000
const MAX_MS = 4 * 60 * 60 * 1000
const POLL_MS = 5_000
const MARK = "● "

export default class extends Controller {
  static targets = ["modal", "error", "startBtn", "pill", "pillDot", "pillSpin", "pillTitle", "pillTime", "pillHint", "waves",
                    "callWave", "micWave", "micNote", "pillStop", "popOut", "tryAgain", "openMeeting", "pillAction",
                    "pipTemplate", "statusText", "mini", "miniTime"]
  static values = { url: String, startUrl: String, i18n: Object, watch: Boolean, launcher: Boolean }

  connect() {
    if (this.launcherValue) return
    clearTimeout(this.goneTimer)
    if (this.wired) return // turbo-permanent: back on the next page, still the same recording
    this.wired = true
    this.queue = []
    this.seq = 0
    this._onBeforeUnload = (event) => {
      if (!this._busy()) return
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", this._onBeforeUnload)
    // Left anyway (closed the tab, reloaded, went to another site) mid-call:
    // the server stops the recording and writes the minutes from the pieces
    // it already has, instead of leaving it "recording" for good (2026-10-08).
    this._onPageHide = () => {
      if (!this.recording || !this.recordingUrl) return
      try {
        fetch(`${this.recordingUrl}/finish`, {
          method: "POST",
          keepalive: true,
          headers: { Accept: "application/json", "Content-Type": "application/json",
                     "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
          body: JSON.stringify({ left: true })
        }).catch(() => {})
      } catch { /* Meeting::CallRecordingSweepJob finishes it */ }
    }
    window.addEventListener("pagehide", this._onPageHide)
    if (this.watchValue) {
      this.recordingUrl = this.urlValue
      this._poll()
    }
  }

  disconnect() {
    if (this.launcherValue) return
    // Moving to another page takes the element out and puts it straight
    // back; only a page without the recorder really ends it.
    this.goneTimer = setTimeout(() => {
      if (this.element.isConnected) return
      this.gone = true
      this.wired = false
      clearTimeout(this.pollTimer)
      window.removeEventListener("beforeunload", this._onBeforeUnload)
      window.removeEventListener("pagehide", this._onPageHide)
      if (this.recording) this.stop()
    }, 0)
  }

  // ── Opening it ──────────────────────────────────────────────────────

  // The New meeting menu's item: the recorder lives in the layout.
  launch() {
    window.dispatchEvent(new CustomEvent("call-recorder:open", { detail: { startUrl: this.startUrlValue } }))
  }

  open(event) {
    if (event?.detail?.startUrl) this.startUrl = event.detail.startUrl
    if (this.recording || this.stopping) return
    this._error("")
    if (this.hasModalTarget) this.modalTarget.hidden = false
  }

  close() {
    if (this.hasModalTarget) this.modalTarget.hidden = true
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Recording ───────────────────────────────────────────────────────

  async start() {
    const devices = navigator.mediaDevices
    // Only Chrome and Edge (Chromium, the browsers with userAgentData) hand
    // over a call's sound; Safari and Firefox share the picture alone.
    const chromium = !!navigator.userAgentData
    if (!chromium || !devices?.getDisplayMedia || typeof window.MediaRecorder === "undefined") return this._error(this._t("unsupported"))
    this._error("")
    this.startBtnTarget.disabled = true

    // Stay on this page after the call's window is picked, so the bar and
    // its Pop out are right here (Chrome otherwise jumps to what's shared).
    let focus = null
    try {
      focus = new window.CaptureController()
      focus.setFocusBehavior?.("no-focus-change")
    } catch {
      focus = null
    }

    // Chrome's picker opens on the right tab (Phelipe 2026-10-08): Window on a
    // Mac, where a window shares the system sound; Entire Screen on Windows,
    // the only choice there with "Share system audio". Chrome never lets a
    // site pick the window itself, nor switch the sound on for them.
    const mac = /mac/i.test(navigator.userAgentData?.platform || navigator.platform || "")
    let display
    try {
      display = await devices.getDisplayMedia({
        video: { frameRate: 1, displaySurface: mac ? "window" : "monitor" },
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        systemAudio: "include",
        selfBrowserSurface: "exclude",
        ...(focus ? { controller: focus } : {})
      })
    } catch (e) {
      this.startBtnTarget.disabled = false
      return this._error(this._t(e?.name === "NotAllowedError" ? "cancelled" : "failed_to_start"))
    }

    const callAudio = display.getAudioTracks()
    if (!callAudio.length) {
      display.getTracks().forEach((track) => track.stop())
      this.startBtnTarget.disabled = false
      return this._error(this._t("no_audio"))
    }

    let mic = null
    try {
      mic = await devices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
    } catch {
      mic = null
    }

    // Start makes the meeting for the call, and its recording.
    const created = await this._json("POST", this.startUrl || this.startUrlValue)
    if (!created?.ok) {
      display.getTracks().forEach((track) => track.stop())
      mic?.getTracks().forEach((track) => track.stop())
      this.startBtnTarget.disabled = false
      return this._error(this._t("failed_to_start"))
    }
    this.recordingUrl = created.recording_url
    this.meetingUrl = created.meeting_url

    // The call's sound and this person's microphone, mixed into one track,
    // with a listener on each for its wave.
    this.audioContext = new AudioContext()
    const mix = this.audioContext.createMediaStreamDestination()
    this.callAnalyser = this._plug(new MediaStream(callAudio), mix)
    this.micAnalyser = mic ? this._plug(mic, mix) : null
    this.streams = [display, mic].filter(Boolean)

    const type = ["audio/webm;codecs=opus", "audio/webm"].find((t) => MediaRecorder.isTypeSupported?.(t)) || ""
    this.recorder = new MediaRecorder(mix.stream, type ? { mimeType: type, audioBitsPerSecond: 32_000 } : { audioBitsPerSecond: 32_000 })
    this.recorder.ondataavailable = (event) => { if (event.data?.size) this._enqueue(event.data) }
    this.recorder.onstop = () => this._afterStop()
    this.recorder.start(PIECE_MS)

    // Sharing stopped from the browser's own bar ends the recording too.
    display.getTracks().forEach((track) => track.addEventListener("ended", () => this.stop("sharing_stopped")))

    this.recording = true
    this.startedAt = Date.now()
    this.startBtnTarget.disabled = false
    this.close()
    if (this.hasMicNoteTarget) this.micNoteTarget.hidden = !!mic
    this._paint("recording")
    this._everySecond()
    this.clock = setInterval(() => this._everySecond(), 1000)
    this._loop()
    this.popOut() // floats straight away where the browser allows it; else the bar's Pop out
  }

  stop(reason) {
    if (!this.recording) return
    this.recording = false
    this.stopping = true
    clearInterval(this.clock)
    this._closePip()
    this._unmarkTitle()
    this.stopReason = typeof reason === "string" ? reason : null
    this._paint("working", this._t("saving"))
    if (this.recorder && this.recorder.state !== "inactive") {
      try { this.recorder.stop() } catch { this._afterStop() }
    } else {
      this._afterStop()
    }
    this.streams?.forEach((stream) => stream.getTracks().forEach((track) => track.stop()))
    this.audioContext?.close?.()
  }

  async tryAgain() {
    if (this.hasTryAgainTarget) this.tryAgainTarget.hidden = true
    const answer = await this._json("POST", `${this.recordingUrl}/try_again`)
    if (answer?.recording) this._show(answer.recording)
    this._poll()
  }

  dismiss() {
    if (this.hasPillTarget) this.pillTarget.hidden = true
  }

  // ── Pop out: a little window on top of every other app ──────────────

  async popOut() {
    if (!this.recording || this.pipWindow || !window.documentPictureInPicture?.requestWindow) return
    let pip
    try {
      pip = await window.documentPictureInPicture.requestWindow({ width: 320, height: 215 })
    } catch {
      return this._paintPopOut() // the browser wants a click first: the bar's Pop out button
    }
    if (!this.recording) return pip.close()
    this.pipWindow = pip
    // The pop-out is the bar now: the page keeps only a small pill, bottom
    // right, in case the little window got lost behind another app (Phelipe
    // 2026-10-08: two full bars, and the page's sat on the menu's links).
    if (this.hasPillTarget) this.pillTarget.hidden = true
    if (this.hasMiniTarget) this.miniTarget.hidden = false
    const page = pip.document
    // Same look as the page: its styles and its theme.
    document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => page.head.appendChild(node.cloneNode(true)))
    Array.from(document.documentElement.attributes).forEach(({ name, value }) => page.documentElement.setAttribute(name, value))
    page.body.style.margin = "0"
    page.body.innerHTML = this.pipTemplateTarget.innerHTML
    page.querySelector("[data-pip='stop']")?.addEventListener("click", () => this.stop())
    pip.addEventListener("pagehide", () => {
      if (this.pipWindow !== pip) return
      this.pipWindow = null
      if (this.recording && this.hasPillTarget) this.pillTarget.hidden = false
      if (this.hasMiniTarget) this.miniTarget.hidden = true
      this._paintPopOut()
      if (this.recording) this._loop()
    })
    this._paintPopOut()
    this._loop()
  }

  _closePip() {
    const pip = this.pipWindow
    this.pipWindow = null
    try { pip?.close() } catch { /* already gone */ }
  }

  // ── Pieces → S3 ─────────────────────────────────────────────────────

  _enqueue(blob) {
    this.seq += 1
    this.queue.push({ seq: this.seq, blob })
    this._pump()
  }

  async _pump() {
    if (this.pumping) return
    this.pumping = true
    let failures = 0
    while (this.queue.length) {
      if (await this._upload(this.queue[0])) {
        this.queue.shift()
        failures = 0
        this.troubled = false
      } else {
        failures += 1
        if (failures >= 2) this.troubled = true
        await new Promise((resolve) => setTimeout(resolve, Math.min(30_000, 1000 * 2 ** failures)))
      }
    }
    this.pumping = false
    if (this.stopped && !this.finished) this._finish()
  }

  async _upload({ seq, blob }) {
    const signed = await this._json("POST", `${this.recordingUrl}/presign`, { seq })
    if (!signed?.url) return signed?.error === "too_long" || signed?.error === "stopped"
    try {
      const put = await fetch(signed.url, { method: "PUT", body: blob, headers: { "Content-Type": "audio/webm" } })
      if (!put.ok) return false
    } catch {
      return false
    }
    return !!(await this._json("POST", `${this.recordingUrl}/piece`, { seq, size: blob.size }))?.ok
  }

  _afterStop() {
    if (this.stopped) return
    this.stopped = true
    if (!this.pumping) this._pump()
  }

  async _finish() {
    this.finished = true
    const answer = await this._json("POST", `${this.recordingUrl}/finish`)
    this.stopping = false
    if (answer?.recording) this._show(answer.recording)
    this._poll()
  }

  // ── Turning it into minutes ─────────────────────────────────────────

  async _poll() {
    clearTimeout(this.pollTimer)
    if (this.gone) return
    const answer = await this._json("GET", this.recordingUrl)
    const recording = answer?.recording
    if (recording) this._show(recording)
    if (recording?.status === "done") {
      // Watching from the meeting page: show the minutes there. Recorded from
      // anywhere else: the bar offers the meeting, without pulling him away.
      if (this.watchValue) this.pollTimer = setTimeout(() => { if (!this.gone) this._go(window.location.href, "replace") }, 1200)
    } else if (recording?.status !== "failed") {
      this.pollTimer = setTimeout(() => this._poll(), POLL_MS)
    }
  }

  _show(recording) {
    let text
    if (recording.status === "done") {
      text = this._t(recording.minutes_count > 0 ? "done" : "done_no_items")
    } else if (recording.status === "failed") {
      text = this._t({ no_audio: "failed_no_audio", unreadable: "failed_unreadable", no_speech: "failed_no_speech" }[recording.error] || "failed_transcription")
    } else if (recording.stage === "transcribing" && recording.segments_count) {
      text = this._t("transcribing", { done: Math.min(recording.segments_done + 1, recording.segments_count), total: recording.segments_count })
    } else {
      text = this._t(recording.stage === "minutes" ? "minutes" : "waiting")
    }
    if (this.stopReason && recording.status !== "failed") text = `${this._t(this.stopReason)} ${text}`
    const canRetry = recording.status === "failed" && recording.error === "transcription_failed"
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = text
    if (this.hasTryAgainTarget) this.tryAgainTarget.hidden = !canRetry
    if (!this.watchValue && this.hasPillTarget && !this.pillTarget.hidden) {
      this._paint(recording.status === "done" ? "done" : recording.status === "failed" ? "failed" : "working", text, { canRetry })
    }
  }

  // ── The bar ─────────────────────────────────────────────────────────

  // recording: time, waves, Pop out, Stop · working: a spinner and how far ·
  // done: Open the meeting · failed: why, and Try again when it can.
  _paint(state, text = "", { canRetry = false } = {}) {
    if (!this.hasPillTarget) return
    const recording = state === "recording"
    this.pillTarget.hidden = recording && !!this.pipWindow
    if (this.hasMiniTarget) this.miniTarget.hidden = !(recording && this.pipWindow)
    this.pillTitleTarget.textContent = recording ? this._t("recording_title") : text
    this.pillTimeTarget.hidden = !recording
    this.wavesTarget.hidden = !recording
    this.pillHintTarget.hidden = !recording
    this.pillHintTarget.textContent = recording ? this._t(this.troubled ? "upload_trouble" : "keep_working") : ""
    if (!recording && this.hasMicNoteTarget) this.micNoteTarget.hidden = true
    this.pillDotTarget.hidden = !recording
    this.pillSpinTarget.hidden = state !== "working"
    this.pillStopTarget.hidden = !recording
    this.tryAgainTarget.hidden = !(state === "failed" && canRetry)
    this.openMeetingTarget.hidden = !(state === "done" && this.meetingUrl)
    if (this.meetingUrl) this.openMeetingTarget.href = this.meetingUrl
    this.pillActionTarget.hidden = !(state === "done" || state === "failed")
    this._paintPopOut()
  }

  _paintPopOut() {
    if (!this.hasPopOutTarget) return
    this.popOutTarget.hidden = !(this.recording && !this.pipWindow && window.documentPictureInPicture?.requestWindow)
  }

  _everySecond() {
    if (!this.recording) return
    if (Date.now() - this.startedAt >= MAX_MS) return this.stop("too_long")
    // The tab says it too, whatever page he's on.
    if (!document.title.startsWith(MARK)) document.title = MARK + document.title
    if (this.hasPillHintTarget) this.pillHintTarget.textContent = this._t(this.troubled ? "upload_trouble" : "keep_working")
    this._showTime()
  }

  _unmarkTitle() {
    if (document.title.startsWith(MARK)) document.title = document.title.slice(MARK.length)
  }

  _showTime() {
    const total = Math.floor((Date.now() - this.startedAt) / 1000)
    const [h, m, s] = [Math.floor(total / 3600), Math.floor(total / 60) % 60, total % 60]
    const clock = h ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`
    if (this.hasPillTimeTarget) this.pillTimeTarget.textContent = clock
    if (this.hasMiniTimeTarget) this.miniTimeTarget.textContent = clock
    const pipTime = this.pipWindow?.document.querySelector("[data-pip='time']")
    if (pipTime) pipTime.textContent = clock
  }

  // ── Waves ───────────────────────────────────────────────────────────

  _plug(stream, mix) {
    const source = this.audioContext.createMediaStreamSource(stream)
    source.connect(mix)
    const analyser = this.audioContext.createAnalyser?.()
    if (!analyser) return null
    analyser.fftSize = 256
    analyser.smoothingTimeConstant = 0.55
    source.connect(analyser)
    return analyser
  }

  // Drawn on the window that's showing: the popped-out one keeps moving even
  // while this tab sits behind Teams.
  _loop() {
    const token = (this.loopToken = (this.loopToken || 0) + 1)
    const host = this.pipWindow || window
    const next = (fn) => (host.requestAnimationFrame ? host.requestAnimationFrame(fn) : setTimeout(fn, 100))
    const step = () => {
      if (token !== this.loopToken || !this.recording) return
      this._showTime()
      const pip = this.pipWindow?.document
      this._wave(this.callAnalyser, [this.hasCallWaveTarget && this.callWaveTarget, pip?.querySelector("[data-pip='call']")])
      this._wave(this.micAnalyser, [this.hasMicWaveTarget && this.micWaveTarget, pip?.querySelector("[data-pip='mic']")])
      next(step)
    }
    next(step)
  }

  // Bars that move with the sound: green while it hears something, grey and
  // flat while it's quiet (or there's no microphone).
  _wave(analyser, canvases) {
    const W = 44, H = 18, BARS = 9, BW = 3, GAP = (W - BARS * BW) / (BARS - 1)
    const levels = []
    if (analyser) {
      const bins = new Uint8Array(analyser.frequencyBinCount)
      analyser.getByteFrequencyData(bins)
      const span = Math.min(bins.length, 48) // speech sits in the low bins
      for (let i = 0; i < BARS; i++) {
        const from = Math.floor(i * span / BARS), to = Math.max(from + 1, Math.floor((i + 1) * span / BARS))
        let sum = 0
        for (let j = from; j < to; j++) sum += bins[j]
        levels.push(sum / (to - from) / 255)
      }
    }
    const heard = levels.some((v) => v > 0.12)
    canvases.filter(Boolean).forEach((canvas) => {
      const g = canvas.getContext?.("2d")
      if (!g) return
      const dpr = canvas.ownerDocument.defaultView?.devicePixelRatio || 1
      if (canvas.width !== W * dpr) { canvas.width = W * dpr; canvas.height = H * dpr }
      g.setTransform(dpr, 0, 0, dpr, 0, 0)
      g.clearRect(0, 0, W, H)
      g.fillStyle = heard ? "#10b981" : "rgba(107, 114, 128, 0.35)"
      for (let i = 0; i < BARS; i++) {
        const h = Math.max(2, Math.min(H, (levels[i] || 0) * H * 1.6))
        g.fillRect(i * (BW + GAP), (H - h) / 2, BW, h)
      }
    })
  }

  // ── Little helpers ──────────────────────────────────────────────────

  _busy() {
    return !!(this.recording || (this.stopping && !this.finished))
  }

  _error(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.hidden = !message
  }

  _t(key, vars = {}) {
    const text = this.i18nValue?.[key] || key
    return text.replace(/%\{(\w+)\}/g, (_, name) => (vars[name] ?? ""))
  }

  _go(url, action = "advance") {
    if (window.Turbo) window.Turbo.visit(url, { action })
    else window.location.href = url
  }

  async _json(method, url, body) {
    try {
      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: method === "GET" ? undefined : JSON.stringify(body || {})
      })
      return await response.json().catch(() => null)
    } catch {
      return null
    }
  }
}
